import { XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InquiryStatusBadge } from './InquiryStatusBadge';
import { MAX_REPLY_LENGTH } from '../services/inquiryService';
import type { InquirySummary, ReplyToInquiryRequest } from '../types/inquiry';
import { formatDate } from '../utils/formatDate';

interface InquiryDetailPanelProps {
  readonly inquiry: InquirySummary;
  readonly isMarkingAsRead: boolean;
  readonly isReplying: boolean;
  readonly isMarkingAsReplied: boolean;
  readonly onMarkAsRead: (id: string) => void;
  readonly onReplyToInquiry: (id: string, request: ReplyToInquiryRequest) => void;
  readonly onMarkAsReplied: (id: string) => void;
  readonly onClose: () => void;
}

export const InquiryDetailPanel = ({
  inquiry,
  isMarkingAsRead,
  isReplying,
  isMarkingAsReplied,
  onMarkAsRead,
  onReplyToInquiry,
  onMarkAsReplied,
  onClose,
}: InquiryDetailPanelProps) => {
  const { t } = useTranslation(['common', 'pages', 'errors']);

  const [showComposer, setShowComposer] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyError, setReplyError] = useState('');

  const isAnyPending = isMarkingAsRead || isReplying || isMarkingAsReplied;
  const isReplied = inquiry.status === 'replied';
  const isNew = inquiry.status === 'new';

  const handleSendReply = () => {
    if (replyText.trim().length === 0) {
      setReplyError(t('errors:replyRequired'));
      return;
    }
    setReplyError('');
    onReplyToInquiry(inquiry.inquiryId, { replyText });
    setShowComposer(false);
    setReplyText('');
  };

  const handleCancelReply = () => {
    setShowComposer(false);
    setReplyText('');
    setReplyError('');
  };

  return (
    <aside
      className="fixed inset-y-0 right-0 w-[480px] bg-white border-l border-gray-200 shadow-xl flex flex-col z-20"
      aria-label={t('pages:inquiryDetailsAriaLabel')}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('pages:inquiryDetailsHeading')}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={t('pages:closePanel')}
        >
          <XCircle size={20} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Meta */}
        <div className="space-y-2 text-sm">
          {inquiry.name && (
            <div className="flex items-center justify-between">
              <span className="text-gray-500">{t('pages:fieldName')}</span>
              <span className="font-medium text-gray-900">{inquiry.name}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('pages:columnSubmitterEmail')}</span>
            <span className="font-medium text-gray-900">{inquiry.email}</span>
          </div>
          {inquiry.customerId && (
            <div className="flex items-center justify-between">
              <span className="text-gray-500">{t('pages:fieldCustomerId')}</span>
              <span className="text-gray-700 font-mono text-xs">{inquiry.customerId}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('pages:columnStatus')}</span>
            <InquiryStatusBadge status={inquiry.status} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('pages:columnSubmitted')}</span>
            <span className="text-gray-700">{formatDate(inquiry.createdAt.slice(0, 10))}</span>
          </div>
        </div>

        {/* Message */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t('pages:fieldMessage')}
          </p>
          <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
            {inquiry.message}
          </p>
        </div>

        {/* Stored reply (when REPLIED) */}
        {isReplied && inquiry.replyText && (
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              {t('pages:sectionReplyText')}
            </p>
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap bg-green-50 border border-green-100 rounded p-3">
              {inquiry.replyText}
            </p>
          </div>
        )}

        {/* Reply composer */}
        {showComposer && (
          <div className="space-y-2">
            <textarea
              value={replyText}
              onChange={e => {
                setReplyText(e.target.value);
                if (replyError) setReplyError('');
              }}
              maxLength={MAX_REPLY_LENGTH}
              placeholder={t('pages:replyPlaceholder')}
              rows={5}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400"
              aria-label={t('pages:replyPlaceholder')}
            />
            <div className="flex justify-between items-center text-xs text-gray-400">
              {replyError
                ? <span className="text-red-600">{replyError}</span>
                : <span />
              }
              <span>{t('common:charCounter', { current: replyText.length, max: MAX_REPLY_LENGTH })}</span>
            </div>
          </div>
        )}
      </div>

      {/* Actions footer */}
      {!isReplied && (
        <div className="px-6 py-4 border-t border-gray-100 space-y-2">
          {!showComposer ? (
            <div className="flex flex-wrap gap-2">
              {/* Mark as Read — only for NEW */}
              {isNew && (
                <button
                  onClick={() => onMarkAsRead(inquiry.inquiryId)}
                  disabled={isAnyPending}
                  className="flex-1 text-sm font-medium px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isMarkingAsRead
                    ? t('pages:markingAsRead')
                    : t('pages:markAsRead')}
                </button>
              )}

              {/* Mark as Replied (no email) */}
              <button
                onClick={() => onMarkAsReplied(inquiry.inquiryId)}
                disabled={isAnyPending}
                className="flex-1 text-sm font-medium px-3 py-2 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMarkingAsReplied
                  ? t('pages:markingAsReplied')
                  : t('pages:markAsReplied')}
              </button>

              {/* Reply (with email) */}
              <button
                onClick={() => setShowComposer(true)}
                disabled={isAnyPending}
                className="flex-1 text-sm font-medium px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pages:reply')}
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleSendReply}
                disabled={isReplying}
                className="flex-1 text-sm font-medium px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isReplying ? t('pages:replying') : t('pages:sendReply')}
              </button>
              <button
                onClick={handleCancelReply}
                disabled={isReplying}
                className="text-sm font-medium px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('pages:cancelReply')}
              </button>
            </div>
          )}
        </div>
      )}
    </aside>
  );
};
